import { connectBluetoothPrinter } from './bluetoothPrinter';

const ESC = 0x1b, GS = 0x1d;
const CMD = {
  INIT: [ESC, 0x40],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  FEED: [ESC, 0x64, 0x02],
  CUT: [GS, 0x56, 0x41, 0x10],
};

export interface BarcodeLabelItem {
  name: string;
  brand?: string;
  rate: number;
  unit?: string;
  barcode: string;           // raw digits, printed as crisp text below the bars
  barcodeImgDataUrl: string; // bars-only PNG data URL from bwip-js canvas.toDataURL()
  qty: number;
}

interface LabelFields {
  name: boolean;
  brand: boolean;
  price: boolean;
  unit: boolean;
  barcodeNumber: boolean;
}

interface LabelOpts {
  widthDots?: number;   // printer raster width — 384 for 58mm @203dpi, 576 for 80mm
  currSym?: string;
  cutBetween?: boolean; // send a partial cut after each physical label (default true)
  fields?: LabelFields; // which parts to include — defaults to everything on
}

const DEFAULT_FIELDS: LabelFields = { name: true, brand: true, price: true, unit: true, barcodeNumber: true };

// Renders one line of text as a canvas bitmap and returns it as an ESC/POS
// raster image (GS v 0). Row-major, top-to-bottom — same orientation the
// printer expects, so this never rotates.
function rasterizeTextLine(text: string, opts: { width?: number; fontSize?: number; align?: 'left' | 'center'; bold?: boolean } = {}): number[] {
  if (!text) return [];
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 24;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;
  ctx.font = `${opts.bold ? 'bold ' : ''}${fontSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  const align = opts.align || 'left';
  ctx.textAlign = align === 'center' ? 'center' : 'left';
  const x = align === 'center' ? canvas.width / 2 : 6;
  ctx.fillText(text, x, canvas.height / 2, canvas.width - 12);

  return canvasToRaster(canvas);
}

// Loads a barcode PNG data URL, scales it to fit the printer width (preserving
// aspect ratio — this is the step that was missing before, and the likely
// source of the rotation: without an explicit target width/height derived
// from the SOURCE image's own aspect ratio, it's easy to accidentally swap
// width/height when building the GS v 0 header), and returns it as an
// ESC/POS raster image.
function rasterizeBarcodeImage(dataUrl: string, widthDots: number): Promise<number[]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = widthDots / img.width;
      const targetW = widthDots;
      const targetH = Math.round(img.height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(img, 0, 0, targetW, targetH);

      resolve(canvasToRaster(canvas));
    };
    img.onerror = () => resolve([]); // invalid barcode image — skip it, don't break the whole label
    img.src = dataUrl;
  });
}

// Shared canvas → ESC/POS GS v 0 raster conversion. width/height are always
// taken directly from the canvas that was actually drawn — never passed in
// separately — so there's no way for them to get swapped.
function canvasToRaster(canvas: HTMLCanvasElement): number[] {
  const ctx = canvas.getContext('2d')!;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        pixels[byteIndex] |= 1 << (7 - (x % 8));
      }
    }
  }
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;
  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}

async function buildBarcodeLabelBytes(item: BarcodeLabelItem, opts: LabelOpts): Promise<number[]> {
  const widthDots = opts.widthDots || 384;
  const sym = opts.currSym || '₹';
  const fields = opts.fields || DEFAULT_FIELDS;

  const nameBytes = fields.name && item.name
    ? rasterizeTextLine(item.name, { width: widthDots, fontSize: 26, align: 'center', bold: true })
    : [];
  const brandBytes = fields.brand && item.brand
    ? rasterizeTextLine(item.brand, { width: widthDots, fontSize: 18, align: 'center' })
    : [];
  const barcodeImgBytes = item.barcodeImgDataUrl ? await rasterizeBarcodeImage(item.barcodeImgDataUrl, widthDots) : [];
  const barcodeTextBytes = fields.barcodeNumber
    ? rasterizeTextLine(item.barcode, { width: widthDots, fontSize: 20, align: 'center' })
    : [];
  const priceBytes = fields.price && item.rate > 0
    ? rasterizeTextLine(`${sym}${item.rate.toFixed(2)}${fields.unit && item.unit ? ' / ' + item.unit : ''}`, { width: widthDots, fontSize: 22, align: 'center' })
    : (fields.unit && item.unit
        ? rasterizeTextLine(`per ${item.unit}`, { width: widthDots, fontSize: 18, align: 'center' })
        : []);

  return [
    ...CMD.ALIGN_CENTER,
    ...nameBytes,
    ...brandBytes,
    ...barcodeImgBytes,
    ...barcodeTextBytes,
    ...priceBytes,
    ...CMD.FEED,
  ];
}

export async function printBarcodeLabelsBT(items: BarcodeLabelItem[], opts: LabelOpts = {}): Promise<boolean> {
  if (items.length === 0) return false;
  try {
    const characteristic = await connectBluetoothPrinter();
    if (!characteristic) return false;

    const allBytes: number[] = [...CMD.INIT];
    for (const item of items) {
      const labelBytes = await buildBarcodeLabelBytes(item, opts);
      for (let i = 0; i < item.qty; i++) {
        allBytes.push(...labelBytes);
        if (opts.cutBetween !== false) allBytes.push(...CMD.CUT);
      }
    }

    const chunk = 20;
    for (let i = 0; i < allBytes.length; i += chunk) {
      await characteristic.writeValue(new Uint8Array(allBytes.slice(i, i + chunk)));
      await new Promise(r => setTimeout(r, 30));
    }
    return true;
  } catch (e) {
    console.error('BT label print failed:', e);
    return false;
  }
}